import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import {
  Account,
  AccountDataRemovalMethod,
  dataRemovalConfirmWord,
} from '@/client/accounts';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { useAccount } from '@/hooks/query';
import {
  useAcceptDpaAmendment,
  useWithdrawDpaAmendment,
} from '@/hooks/query/osint.hooks';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import {
  loadCurrentUserAction,
  selectCurrentUser,
} from '@/store/features/users';
import { AppButton } from '@/ui';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { InlineMenu } from '@/ui/inline-menu';
import { FlexContainer } from '@/ui/styled-ui';
import { InfoCircleIcon } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { RadioButton } from 'primereact/radiobutton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { AccountHeader } from './AccountHeader';

const getPathItems = (
  account: Account | undefined,
  currentAccount: Account,
  can: (action: Actions, subject: Subjects) => boolean,
  t: TranslationFunctionType,
): MenuItem[] => {
  const pathItems: MenuItem[] = [];

  pathItems.push({
    label: currentAccount?.name,
    url: !currentAccount.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
    template: AppBreadCrumbTemplate,
  });

  if (
    currentAccount?.isSystem &&
    account &&
    can(Actions.READ, Subjects.ACCOUNTS)
  ) {
    pathItems.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  pathItems.push({
    label: t('account.dataControl'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_DATA_CONTROL](
          account?.id as string,
        )
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_DATA_CONTROL](),
    template: AppBreadCrumbTemplate,
  });

  return pathItems;
};

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-bottom: 1px solid var(--gray-dark);
  padding-bottom: var(--xlarge-padding);

  &:not(:first-child) {
    padding-top: var(--xlarge-padding);
  }
`;

const StyledHeading = styled.h3`
  color: var(--black-main);
  font-size: 19px;
  line-height: 150%; /* 28.5px */
  margin: 0;
  font-weight: 600;
`;

const StyledDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  margin-top: 7px;
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
`;

const StyledCheckboxLabel = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--small-font-size);
  cursor: pointer;
  font-weight: 500;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: default;
    `}
`;

const StyledRadioContainer = styled.div`
  display: flex;
  gap: var(--default-padding);
  padding: 28px;
  border-radius: var(--xsmall-border-radius);
  background: var(--beige-main);
  margin-top: var(--default-padding);

  > div {
    flex: 1;
  }
`;

const StyledRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
  cursor: pointer;
  font-weight: 500;
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: var(--default-padding);
`;

const StyledInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--small-font-size);
  font-weight: 500;
`;

const StyledInputWrapper = styled.div`
  width: 100%;

  .p-inputnumber {
    width: 100%;
  }
`;

const StyledCheckboxWithInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: var(--medium-padding);
`;

const StyledCheckboxInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  width: 100%;
`;

const StyledWarningSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--red-pale);
  padding: 28px;
  border-radius: var(--default-border-radius);
  margin-top: 28px;
  border-radius: 10px;
  border-bottom: none;

  .transparent-background.p-button.p-button-outlined {
    background: transparent !important;

    &:hover {
      background: var(--red-dark) !important;
    }
  }
`;

const StyledWarningHeading = styled(StyledHeading)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledWarningDescription = styled(StyledDescription)`
  margin-top: var(--small-padding);

  span {
    color: var(--red-main);
  }
`;

const StyledWarningText = styled.span`
  color: var(--red-main);
  font-weight: 600;
`;

const StyledWarningButton = styled(AppButton)`
  margin-top: 0;
`;

const StyledDeleteModal = styled(Dialog)`
  width: 550px;

  .p-dialog-content {
    padding: var(--big-padding);
  }

  .p-dialog-footer {
    padding: var(--big-padding);
    padding-top: 0;
  }
`;

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: var(--small-padding);
`;

const StyledModalText = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);

  strong {
    font-weight: 600;
  }

  &.user-select-none {
    user-select: none;
  }
`;

const StyledConfirmationInput = styled(InputText)`
  width: 100%;
  margin-top: 8px;
`;

const StyledModalFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 8px;
`;

const StyledOsintRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--medium-big-padding);
  margin-top: var(--default-padding);
`;

const StyledOsintLeft = styled.div`
  flex: 1;
`;

const StyledOsintSubHeading = styled.h4`
  color: var(--black-main);
  font-size: var(--medium-font-size);
  font-weight: 600;
  line-height: 150%;
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
`;

const StyledInfoToast = styled.div`
  background: var(--blue-light);
  border-radius: var(--small-border-radius);
  padding: 18px var(--default-padding) 20px 52px;
  margin-top: var(--default-padding);
  position: relative;

  &::before {
    content: '\\e924';
    font-family: 'primeicons';
    position: absolute;
    left: var(--default-padding-small);
    top: 18px;
    width: 24px;
    height: 24px;
    color: var(--blue-dark);
    font-size: var(--medium-font-size);
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const StyledInfoText = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 500;

  &:not(:first-child) {
    margin-top: var(--xsmall-padding);
  }
`;

const StyledAttribution = styled.p`
  margin: 0;
  margin-top: var(--xsmall-padding);
  font-size: var(--xsmall-font-size);
  font-style: italic;
  opacity: 0.8;
`;

const StyledWithdrawModal = styled(Dialog)`
  border-radius: var(--medium-border-radius);

  .p-dialog-content {
    padding: var(--semi-big-padding);
  }

  .p-dialog-header {
    display: none;
  }
`;

const StyledWithdrawTitle = styled.h2`
  font-size: var(--modal-heading-font-size);
  font-weight: 700;
  line-height: var(--default-line-height);
  margin: 0 0 var(--medium-padding);
`;

const StyledWithdrawBody = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);

  &:not(:first-of-type) {
    margin-top: var(--medium-padding);
  }
`;

const StyledWithdrawFooter = styled.div`
  display: flex;
  gap: var(--semi-xsmall-padding);
  margin-top: var(--medium-big-padding);
`;

const StyledDestructiveButton = styled(AppButton)`
  &.p-button {
    background: var(--red-dark);
    border-color: var(--red-dark);
    border-radius: var(--default-border-radius);
    color: var(--white-main);

    &:hover {
      background: var(--red-dark-hover);
      border-color: var(--red-dark-hover);
    }
  }
`;

const StyledCancelButton = styled(AppButton)`
  &.p-button.p-button-outlined {
    border-radius: var(--default-border-radius);
    border-color: var(--purple-dark);
    color: var(--purple-dark);

    &:hover {
      background: var(--black-opacity-subtle);
    }
  }
`;

export const AccountDataControlPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { can } = usePermission();
  const { canUseFeature } = useFeatureFlag();
  const accountId = currentAccount?.isSystem ? id : currentAccount?.id;
  const {
    account,
    isLoading: isAccountLoading,
    refetch: refetchAccount,
  } = useAccount({
    accountId,
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [allowPosthogTracking, setAllowPosthogTracking] =
    useState<boolean>(false);
  const [dataRemovalMethod, setDataRemovalMethod] =
    useState<AccountDataRemovalMethod>(AccountDataRemovalMethod.DELETE);
  const [gracePeriodDays, setGracePeriodDays] = useState<number>(30);
  const [autoDeleteDeactivated, setAutoDeleteDeactivated] =
    useState<boolean>(true);
  const [deactivatedDeleteAfterDays, setDeactivatedDeleteAfterDays] =
    useState<number>(30);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [osintChecked, setOsintChecked] = useState<boolean>(false);

  const savedValuesRef = useRef({
    allowPosthogTracking: false,
    dataRemovalMethod: AccountDataRemovalMethod.DELETE,
    gracePeriodDays: 30,
    autoDeleteDeactivated: true,
    effectiveAutoDeleteDays: 30 as number | null,
    osintChecked: false,
  });

  const [showWithdrawModal, setShowWithdrawModal] = useState<boolean>(false);
  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);

  const { acceptDpa, isLoading: isAccepting } = useAcceptDpaAmendment(
    accountId ?? '',
  );
  const { withdrawDpa } = useWithdrawDpaAmendment(accountId ?? '');

  const isSuperAdminOrDeveloper =
    currentUser?.role.code === SystemRoles.SUPER_ADMIN ||
    currentUser?.role.code === SystemRoles.DEVELOPER;

  const dataControlSectionRef = useRef<HTMLDivElement>(null);
  const analyticsSectionRef = useRef<HTMLDivElement>(null);
  const dataEnrichmentSectionRef = useRef<HTMLDivElement>(null);

  const showOsintSection = canUseFeature(
    Subjects.RECON_AI_GENERATED_CAMPAIGN_TEMPLATE,
  );

  const isDirty = useMemo(() => {
    const saved = savedValuesRef.current;
    const effectiveAutoDeleteDays = autoDeleteDeactivated
      ? deactivatedDeleteAfterDays
      : null;

    return (
      allowPosthogTracking !== saved.allowPosthogTracking ||
      dataRemovalMethod !== saved.dataRemovalMethod ||
      gracePeriodDays !== saved.gracePeriodDays ||
      autoDeleteDeactivated !== saved.autoDeleteDeactivated ||
      effectiveAutoDeleteDays !== saved.effectiveAutoDeleteDays ||
      osintChecked !== saved.osintChecked
    );
  }, [
    allowPosthogTracking,
    dataRemovalMethod,
    gracePeriodDays,
    autoDeleteDeactivated,
    deactivatedDeleteAfterDays,
    osintChecked,
  ]);

  const menuItems = useMemo(
    () => [
      ...(canUseFeature(Subjects.ACCOUNT_DATA_REMOVAL)
        ? [
            {
              label: t('account.dataControl'),
            },
          ]
        : []),
      { label: t('account.analytics.and.tracking') },
      ...(showOsintSection
        ? [{ label: t('account.dataEnrichment.settings') }]
        : []),
    ],
    [t, showOsintSection],
  );

  const sectionRefs = useMemo(
    () => [
      ...(canUseFeature(Subjects.ACCOUNT_DATA_REMOVAL)
        ? [dataControlSectionRef]
        : []),
      analyticsSectionRef,
      ...(showOsintSection ? [dataEnrichmentSectionRef] : []),
    ],
    [showOsintSection],
  );

  const scrollToSection = (index: number) => {
    setActiveIndex(index);
    const section = sectionRefs[index]?.current;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const items = useMemo(
    () =>
      menuItems.map((item, index) => ({
        ...item,
        className: activeIndex === index ? 'active' : '',
        command: () => scrollToSection(index),
      })),
    [activeIndex, menuItems],
  );

  useEffect(() => {
    if (account) {
      const normalizedDataRemovalMethod =
        account.dataRemovalMethod === AccountDataRemovalMethod.ANONYMIZE
          ? AccountDataRemovalMethod.ANONYMIZE
          : AccountDataRemovalMethod.DELETE;

      savedValuesRef.current = {
        allowPosthogTracking: account.allowPosthogTracking ?? false,
        dataRemovalMethod: normalizedDataRemovalMethod,
        gracePeriodDays: account.gracePeriodDays ?? 30,
        autoDeleteDeactivated: account.enableAutoDeleteInactiveUsers ?? false,
        effectiveAutoDeleteDays: account.enableAutoDeleteInactiveUsers
          ? account.autoDeleteInactiveUsersDays ?? 30
          : null,
        osintChecked: account.osintEnabled ?? false,
      };

      setAllowPosthogTracking(account.allowPosthogTracking ?? false);
      setGracePeriodDays(account.gracePeriodDays ?? 30);

      if (account.dataRemovalMethod === AccountDataRemovalMethod.ANONYMIZE) {
        setDataRemovalMethod(AccountDataRemovalMethod.ANONYMIZE);
      } else {
        setDataRemovalMethod(AccountDataRemovalMethod.DELETE);
      }

      setAutoDeleteDeactivated(account.enableAutoDeleteInactiveUsers ?? false);
      setDeactivatedDeleteAfterDays(account.autoDeleteInactiveUsersDays ?? 30);
      setOsintChecked(account.osintEnabled ?? false);
    }
  }, [account]);

  useEffect(() => {
    const sectionElements = sectionRefs
      .map((ref) => ref.current)
      .filter((el) => el !== null) as HTMLElement[];

    if (sectionElements.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      let maxRatio = 0;
      let activeSectionIndex = 0;

      entries.forEach((entry) => {
        const index = sectionElements.findIndex((el) => el === entry.target);
        if (index !== -1 && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          activeSectionIndex = index;
        }
      });

      if (maxRatio > 0.1) {
        setActiveIndex(activeSectionIndex);
      }
    });

    sectionElements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [account, sectionRefs]);

  const handleSave = async () => {
    if (!account?.id) return false;
    setIsSubmitting(true);

    try {
      const hasTrackingValueChanged =
        account.allowPosthogTracking !== allowPosthogTracking;

      await client.accounts.updateAccount({
        accountId: account.id,
        updates: {
          allowPosthogTracking,
          ...(canUseFeature(Subjects.ACCOUNT_DATA_REMOVAL) && {
            gracePeriodDays,
            dataRemovalMethod,
            enableAutoDeleteInactiveUsers: autoDeleteDeactivated,
            autoDeleteInactiveUsersDays: autoDeleteDeactivated
              ? deactivatedDeleteAfterDays
              : null,
          }),
        },
      });

      if (hasTrackingValueChanged) {
        if (account.id === currentAccount?.id) {
          const accountData = await client.accounts.getAccount(account.id);
          dispatch(setCurrentAccount(accountData));
          await loadCurrentUserAction(dispatch);
        }
      }

      const shouldAcceptDpa =
        osintChecked && !account.osintEnabled && accountId;
      if (shouldAcceptDpa) {
        try {
          await acceptDpa();
          await refetchAccount();
          toast?.success(t('toast.success'), t('osint.dpa.accepted.success'));
        } catch (dpaError) {
          setOsintChecked(false);
          throw dpaError;
        }
      } else {
        await refetchAccount();
        toast?.success(t('toast.success'), t('account.updated'));
      }
      return true;
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOsintCheckboxChange = (checked: boolean) => {
    if (!checked && account?.osintEnabled) {
      setShowWithdrawModal(true);
      return;
    }
    setOsintChecked(checked);
  };

  const handleWithdrawConfirm = async () => {
    setIsWithdrawing(true);
    try {
      await withdrawDpa();
      await refetchAccount();
      setOsintChecked(false);
      setShowWithdrawModal(false);
      toast?.success(t('toast.success'), t('osint.dpa.withdrawn.success'));
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleWithdrawCancel = () => {
    setShowWithdrawModal(false);
  };

  const handleDeleteAnonymizedUsers = async () => {
    if (!account?.id) return;

    setIsDeleting(true);

    try {
      await client.users.deleteAnonymizedUsers(account.id);
      toast?.success(
        t('toast.success'),
        t('account.dataControl.manual.deletion.modal.success'),
      );
      setShowDeleteModal(false);
      setDeleteConfirmation('');
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmation('');
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmation('');
  };

  const isDeleteButtonDisabled =
    deleteConfirmation.trim() !== dataRemovalConfirmWord || isDeleting;

  return (
    <>
      {isAccountLoading || !account ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          {currentAccount && (
            <AppBreadCrumb
              model={getPathItems(account, currentAccount, can, t)}
            />
          )}

          <AccountHeader
            selectedAccount={account}
            title={t('account.dataControl')}
            isDirty={isDirty}
            onSave={handleSave}
            isSaving={isSubmitting || isAccepting}
          />

          <FlexContainer
            gap="var(--big-padding)"
            justify="flex-start"
            align="flex-start"
            style={{ marginTop: 'var(--large-padding)' }}
          >
            <InlineMenu items={items} />
            <div>
              {canUseFeature(Subjects.ACCOUNT_DATA_REMOVAL) && (
                <StyledSection ref={dataControlSectionRef} id="data-control">
                  <StyledHeading>
                    {t('account.dataControl.retention.title')}
                  </StyledHeading>
                  <StyledDescription>
                    {t('account.dataControl.retention.description')}
                  </StyledDescription>

                  <StyledRadioContainer>
                    <div>
                      <StyledHeading style={{ fontSize: '16px' }}>
                        {t('account.dataControl.removal.method.title')}
                      </StyledHeading>
                      <StyledDescription
                        dangerouslySetInnerHTML={{
                          __html: t(
                            'account.dataControl.removal.method.description',
                          ),
                        }}
                      />
                    </div>
                    <FlexContainer
                      direction="column"
                      gap={11}
                      align="flex-start"
                    >
                      <StyledRadioLabel htmlFor="pseudonymize">
                        <RadioButton
                          inputId="pseudonymize"
                          name="dataRemovalMethod"
                          value={AccountDataRemovalMethod.ANONYMIZE}
                          type="radio"
                          className="p-circle"
                          checked={
                            dataRemovalMethod ===
                            AccountDataRemovalMethod.ANONYMIZE
                          }
                          onChange={(e) => {
                            setDataRemovalMethod(e.value);
                          }}
                        />
                        <span>
                          {t('account.dataControl.removal.method.pseudonymize')}
                        </span>
                      </StyledRadioLabel>
                      <StyledRadioLabel htmlFor="delete">
                        <RadioButton
                          inputId="delete"
                          name="dataRemovalMethod"
                          value={AccountDataRemovalMethod.DELETE}
                          type="radio"
                          className="p-circle"
                          checked={
                            dataRemovalMethod ===
                            AccountDataRemovalMethod.DELETE
                          }
                          onChange={(e) => {
                            setDataRemovalMethod(e.value);
                          }}
                        />
                        <span>
                          {t('account.dataControl.removal.method.delete')}
                        </span>
                      </StyledRadioLabel>
                    </FlexContainer>
                  </StyledRadioContainer>

                  <div style={{ marginTop: '28px' }}>
                    <StyledHeading style={{ fontSize: '16px' }}>
                      {t('account.dataControl.automatic.cleanup.title')}
                    </StyledHeading>
                    <StyledDescription>
                      {t('account.dataControl.automatic.cleanup.description')}
                    </StyledDescription>
                    <StyledInputContainer>
                      <StyledInputLabel htmlFor="grace-period">
                        {t('account.dataControl.automatic.cleanup.gracePeriod')}
                        <AppIconTooltip
                          target="grace-period-tooltip"
                          content={t(
                            'account.dataControl.automatic.cleanup.gracePeriod.tooltip',
                          )}
                          position="right"
                        />
                      </StyledInputLabel>
                      <StyledInputWrapper>
                        <InputNumber
                          inputId="grace-period"
                          value={gracePeriodDays}
                          onValueChange={(e) => {
                            setGracePeriodDays(e.value ?? 30);
                          }}
                          min={0}
                          showButtons
                          suffix={` ${t('generic.units.days')}`}
                        />
                      </StyledInputWrapper>
                    </StyledInputContainer>
                    <StyledCheckboxWithInput>
                      <StyledCheckboxContainer>
                        <Checkbox
                          inputId="auto-delete-deactivated"
                          checked={autoDeleteDeactivated}
                          onChange={(e) => {
                            setAutoDeleteDeactivated(e.checked ?? false);
                          }}
                        />
                        <StyledCheckboxLabel htmlFor="auto-delete-deactivated">
                          {t(
                            'account.dataControl.automatic.cleanup.deleteDeactivated',
                          )}
                          <AppIconTooltip
                            target="delete-deactivated-tooltip"
                            content={t(
                              'account.dataControl.automatic.cleanup.deleteDeactivated.tooltip',
                            )}
                            position="right"
                          />
                        </StyledCheckboxLabel>
                      </StyledCheckboxContainer>
                      <StyledCheckboxInputRow>
                        <StyledInputWrapper>
                          <InputNumber
                            inputId="deactivated-delete-after"
                            value={deactivatedDeleteAfterDays}
                            onValueChange={(e) => {
                              setDeactivatedDeleteAfterDays(e.value ?? 30);
                            }}
                            min={0}
                            showButtons
                            suffix={` ${t('generic.units.days')}`}
                            disabled={!autoDeleteDeactivated}
                          />
                        </StyledInputWrapper>
                      </StyledCheckboxInputRow>
                    </StyledCheckboxWithInput>
                  </div>
                  <StyledWarningSection>
                    <div>
                      <StyledWarningHeading>
                        <FlexContainer
                          gap={11}
                          justify="flex-start"
                          align="center"
                        >
                          <InfoCircleIcon />
                          {t('account.dataControl.manual.deletion.title')}
                        </FlexContainer>
                      </StyledWarningHeading>
                      <StyledWarningDescription>
                        <p className="m-0">
                          {t('account.dataControl.manual.deletion.description')}
                        </p>
                        <StyledWarningText>
                          {t('account.dataControl.manual.deletion.warning')}
                        </StyledWarningText>
                      </StyledWarningDescription>
                    </div>
                    <StyledWarningButton
                      type="outlined"
                      className="transparent-background"
                      label={t('account.dataControl.manual.deletion.button')}
                      state={LoadingStatuses.IDLE}
                      onClick={handleOpenDeleteModal}
                    />
                  </StyledWarningSection>
                </StyledSection>
              )}

              <StyledSection ref={analyticsSectionRef} id="analytics-tracking">
                <StyledHeading>
                  {t('account.analytics.and.tracking.settings')}
                </StyledHeading>
                <StyledDescription>
                  {t('account.analytics.and.tracking.description')}
                </StyledDescription>
                <StyledCheckboxContainer>
                  <Checkbox
                    inputId="posthog-processing"
                    checked={allowPosthogTracking}
                    disabled={!isSuperAdminOrDeveloper}
                    onChange={(e) => {
                      setAllowPosthogTracking(e.checked ?? false);
                    }}
                  />
                  <StyledCheckboxLabel
                    htmlFor="posthog-processing"
                    disabled={!isSuperAdminOrDeveloper}
                  >
                    {t('account.analytics.and.tracking.posthog.label')}
                    <AppIconTooltip
                      target="posthog-tooltip"
                      content={t(
                        'account.analytics.and.tracking.posthog.tooltip',
                      )}
                      position="right"
                    />
                  </StyledCheckboxLabel>
                </StyledCheckboxContainer>
              </StyledSection>

              {showOsintSection && (
                <StyledSection
                  ref={dataEnrichmentSectionRef}
                  id="data-enrichment"
                >
                  <StyledHeading>
                    {t('account.dataEnrichment.settings')}
                  </StyledHeading>
                  <StyledDescription>
                    {t('account.dataEnrichment.settings.description')}
                  </StyledDescription>

                  <StyledOsintRow>
                    <StyledOsintLeft>
                      <StyledOsintSubHeading>
                        {t('account.dataEnrichment.osint.title')}
                        <AppIconTooltip
                          target="osint-tooltip"
                          content={t('account.dataEnrichment.osint.tooltip')}
                          position="right"
                        />
                      </StyledOsintSubHeading>
                      <StyledDescription>
                        {t('account.dataEnrichment.osint.description')}
                      </StyledDescription>

                      <StyledCheckboxContainer>
                        <Checkbox
                          inputId="osint-dpa"
                          checked={osintChecked}
                          onChange={(e) =>
                            handleOsintCheckboxChange(e.checked ?? false)
                          }
                        />
                        <StyledCheckboxLabel htmlFor="osint-dpa">
                          {t('account.dataEnrichment.osint.checkbox')}
                        </StyledCheckboxLabel>
                      </StyledCheckboxContainer>

                      {osintChecked && !account.osintEnabled && (
                        <StyledInfoToast>
                          <StyledInfoText
                            dangerouslySetInnerHTML={{
                              __html: t(
                                'account.dataEnrichment.osint.toast.line1',
                              ),
                            }}
                          />
                          <StyledInfoText>
                            {t('account.dataEnrichment.osint.toast.line2')}
                          </StyledInfoText>
                        </StyledInfoToast>
                      )}

                      {account.osintEnabled &&
                        account.dpaAmendmentAcceptedAt &&
                        account.dpaAmendmentAcceptedByName && (
                          <StyledAttribution>
                            {t('account.dataEnrichment.osint.acceptedBy', {
                              name: account.dpaAmendmentAcceptedByName,
                              date: new Date(
                                account.dpaAmendmentAcceptedAt,
                              ).toLocaleDateString(),
                              time: new Date(
                                account.dpaAmendmentAcceptedAt,
                              ).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              }),
                            })}
                          </StyledAttribution>
                        )}
                    </StyledOsintLeft>
                    <div>
                      <AppButton
                        label={t('account.dataEnrichment.osint.reviewDpa')}
                        type="outlined"
                        severity="secondary"
                        state={LoadingStatuses.IDLE}
                        onClick={async () => {
                          try {
                            const { downloadUrl } =
                              await client.accountOsint.getDpaDocument(
                                accountId as string,
                              );
                            window.open(downloadUrl, '_blank');
                          } catch (error) {
                            handleAxiosError(
                              error as Error | AxiosError,
                              toast,
                            );
                          }
                        }}
                      />
                    </div>
                  </StyledOsintRow>
                </StyledSection>
              )}
            </div>
          </FlexContainer>

          <StyledWithdrawModal
            visible={showWithdrawModal}
            onHide={handleWithdrawCancel}
            modal
            draggable={false}
            closable={false}
            pt={{ root: { dir: document.documentElement.dir || 'ltr' } }}
          >
            <StyledWithdrawTitle>
              {t('osint.withdraw.dialog.title')}
            </StyledWithdrawTitle>
            <StyledWithdrawBody>
              {t('osint.withdraw.dialog.body1')}
            </StyledWithdrawBody>
            <StyledWithdrawBody>
              {t('osint.withdraw.dialog.body2')}
            </StyledWithdrawBody>
            <StyledWithdrawFooter>
              <StyledDestructiveButton
                label={t('osint.withdraw.dialog.confirm')}
                state={
                  isWithdrawing ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                }
                onClick={handleWithdrawConfirm}
              />
              <StyledCancelButton
                label={t('osint.withdraw.dialog.cancel')}
                type="outlined"
                severity="secondary"
                isDisabled={isWithdrawing}
                state={LoadingStatuses.IDLE}
                onClick={handleWithdrawCancel}
              />
            </StyledWithdrawFooter>
          </StyledWithdrawModal>

          <StyledDeleteModal
            visible={showDeleteModal}
            onHide={handleCloseDeleteModal}
            header={t('account.dataControl.manual.deletion.modal.title')}
            className="v2"
            modal
            draggable={false}
            closable
            footer={
              <StyledModalFooter>
                <AppButton
                  label={t('button.cancel')}
                  severity="danger"
                  onClick={handleCloseDeleteModal}
                  state={LoadingStatuses.IDLE}
                />
                <AppButton
                  label={t(
                    'account.dataControl.manual.deletion.modal.deleteButton',
                  )}
                  severity="secondary"
                  type="outlined"
                  onClick={handleDeleteAnonymizedUsers}
                  state={
                    isDeleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                  }
                  isDisabled={isDeleteButtonDisabled}
                />
              </StyledModalFooter>
            }
          >
            <StyledModalContent>
              <StyledModalText
                dangerouslySetInnerHTML={{
                  __html: t(
                    'account.dataControl.manual.deletion.modal.description1',
                  ),
                }}
              />
              <StyledModalText
                dangerouslySetInnerHTML={{
                  __html: t(
                    'account.dataControl.manual.deletion.modal.description2',
                  ),
                }}
              />
              <StyledModalText
                className="user-select-none"
                dangerouslySetInnerHTML={{
                  __html: t(
                    'account.dataControl.manual.deletion.modal.description3',
                    { word: dataRemovalConfirmWord },
                  ),
                }}
              />
              <StyledConfirmationInput
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={t(
                  'account.dataControl.manual.deletion.modal.inputPlaceholder',
                )}
              />
            </StyledModalContent>
          </StyledDeleteModal>
        </>
      )}
    </>
  );
};
